/*
 * Choosing a variant and quick view, loaded on every page from layout/theme.liquid.
 *
 * A file, not a snippet's {% javascript %}: Shopify ships a snippet's script only on pages
 * whose first render includes that snippet. Quick view's panel arrives later from a second
 * request, and so do the cards in "Wears well with", so their scripts never came.
 */

/*
 * The picker, for any panel that renders snippets/variant-picker.liquid: quick view
 * (below) and the product page (sections/product.liquid).
 *
 * Keeps the price, the stock line, the add buttons and the form's variant id in step
 * with the radios, and refuses to add until every option is picked. Adding goes
 * through assets/cart-add.js, the path the hero and the stories share. After every
 * pick the root hears `variant:change` with `{ variant, open }` -- `open` while a
 * size is still to choose -- so a panel can follow along (the gallery, the URL).
 */
window.variantPicker = function variantPicker(root) {
  const text = JSON.parse(root.querySelector('[data-qv-strings]').textContent);
  const variants = JSON.parse(root.querySelector('[data-qv-variants]').textContent);
  const radios = [...root.querySelectorAll('[data-qv-option]')];
  const image = root.querySelector('.qv__image');
  const field = root.querySelector('[data-qv-id]');
  const form = field.form;
  const buttons = [...root.querySelectorAll('[data-qv-add]')];
  const stock = root.querySelector('[data-qv-stock]');
  const now = root.querySelector('[data-qv-now]');
  const was = root.querySelector('[data-qv-was]');
  const sale = root.querySelector('[data-qv-sale]');
  const guideButtons = [...root.querySelectorAll('[data-size-guide-select]')];
  const guideDialogs = [...root.querySelectorAll('[data-size-guide-dialog]')];
  const guideOpeners = [...root.querySelectorAll('[data-size-guide-open]')];
  // Opened from a section with Show prices off: the button names no price either.
  const priceless = root.classList.contains('qv-dialog--no-price');
  let showing = field.value;

  const labels = (fn) =>
    buttons.forEach((button) => {
      button.querySelector('[data-qv-add-label]').textContent = fn(button);
    });
  const addLabel = (variant) => (button) =>
    priceless || button.hasAttribute('data-qv-plain') ? text.addPlain : text.add.replace('@', variant.price);

  const chosen = () => {
    const out = [];
    radios.forEach((radio) => {
      if (radio.checked) out[Number(radio.dataset.qvOption) - 1] = radio.value;
    });
    return out;
  };

  // A slot nobody has filled yet matches every value, so a size that has
  // not been picked never strikes through a colourway.
  const fitting = (options) =>
    variants.filter((variant) =>
      variant.options.every((value, i) => options[i] === undefined || value === options[i])
    );

  // The stock line and what kind of line it is -- low, out or choose -- for a panel to style.
  const say = (message, tone = '') => {
    stock.textContent = message;
    stock.dataset.tone = tone;
  };

  // The cart said no: sold out while she was reading, or the last one is already in her bag.
  // Shopify's sentence goes on the stock line -- a phone's half-width button showed a few
  // clipped words of it -- and is brought on screen, because on a phone that line is under the
  // fold the pinned bar was pressed from. A piece that is already hers is shown to her: the
  // bag opens on it, which answers "did it go in?" better than any sentence.
  const refuse = (message) => {
    say(message || text.refused, 'refused');
    stock.scrollIntoView({
      block: 'nearest',
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    if (document.querySelector(`.bag__line[data-variant="${field.value}"]`)) {
      document.querySelector('cart-drawer')?.open(buttons[0]);
    }
  };

  const announce = (variant, open) =>
    root.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, open } }));

  // The guide is a true modal on both the product page and inside quick view. The
  // newest dialog enters the browser's top layer, Escape closes only that layer,
  // and closing returns the keyboard to the button that opened it.
  guideOpeners.forEach((opener) => {
    opener.addEventListener('click', () => {
      const dialog = guideDialogs.find((candidate) => candidate.id === opener.getAttribute('aria-controls'));
      if (!dialog || dialog.open) return;
      dialog.sizeGuideOpener = opener;
      dialog.showModal();
      dialog.querySelector('[data-size-guide-close]')?.focus();
    });
  });

  guideDialogs.forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-size-guide-close]')) dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog.sizeGuideOpener?.focus({ preventScroll: true });
      dialog.sizeGuideOpener = null;
    });
  });

  const sync = () => {
    const picked = chosen();

    // A value that cannot be combined with the rest of the selection is
    // struck through rather than hidden: the colourway does exist. A size that is
    // one piece, in stock with three or fewer left, carries its count ("2 left")
    // for a panel to print on it, so scarcity is seen before the tap, not after.
    radios.forEach((radio) => {
      const trial = picked.slice();
      trial[Number(radio.dataset.qvOption) - 1] = radio.value;
      const matches = fitting(trial);
      const label = radio.nextElementSibling;
      const gone = !matches.some((variant) => variant.available);
      label.classList.toggle('is-unavailable', gone);

      const one = !('qvColour' in radio.dataset) && matches.length === 1 ? matches[0] : null;
      const scarce = one?.available && one.left > 0 && one.left <= 3 ? text.sizeLeft.replace('@', one.left) : '';
      if (scarce) label.dataset.left = scarce;
      else delete label.dataset.left;

      // A struck number and a rust square are for the eye. A screen reader is reading the label,
      // so the label carries the same in words: "38, sold out", "38, 2 left".
      const said = gone ? text.soldOut : scarce;
      let note = label.querySelector('[data-qv-note]');
      if (said && !note) {
        note = Object.assign(document.createElement('span'), { className: 'visually-hidden' });
        note.dataset.qvNote = '';
        label.append(note);
      }
      if (note && said) note.textContent = `, ${said}`;
      else if (note) note.remove();

      // The conversion table is another way to choose the same radio. Keep its
      // availability and selected state identical to the primary size row.
      guideButtons
        .filter(
          (button) =>
            button.dataset.sizeGuideSelect === radio.value &&
            Number(button.dataset.sizeGuideOption) === Number(radio.dataset.qvOption)
        )
        .forEach((button) => {
          button.disabled = gone;
          button.classList.toggle('is-selected', radio.checked);
          button.setAttribute('aria-pressed', radio.checked ? 'true' : 'false');
        });
    });

    picked.forEach((value, i) => {
      const slot = root.querySelector('[data-qv-option-value="' + (i + 1) + '"]');
      const radio = radios.find((choice) => Number(choice.dataset.qvOption) === i + 1 && choice.value === value);
      if (slot) slot.textContent = radio?.dataset.qvLabel || value;
    });

    root.querySelectorAll('.qv__option.is-missing').forEach((set) => {
      if (set.querySelector(':checked')) set.classList.remove('is-missing');
    });

    // Still choosing: show what the picks so far look like and cost, but keep
    // the id empty so nothing reaches the bag until the size is chosen.
    const open = variants[0].options.some((_, i) => picked[i] === undefined);
    const matches = fitting(picked);
    const variant = open
      ? matches.find((candidate) => candidate.available && (candidate.amount === undefined || candidate.amount > 0)) ||
        matches.find((candidate) => candidate.available) ||
        matches[0]
      : matches[0];

    buttons.forEach((button) => button.classList.remove('is-added'));

    if (!variant) {
      field.value = '';
      buttons.forEach((button) => (button.disabled = true));
      labels(() => text.unavailable);
      say(text.unavailable);
      announce(null, true);
      return;
    }

    field.value = open ? '' : variant.id;
    now.textContent = variant.price;
    sale.hidden = !variant.was;
    if (variant.was) was.textContent = variant.was;

    // Only on a real change: the rendered <img> and the JSON spell the same
    // picture differently, so assigning on load would refetch it.
    if (image && variant.image && variant.id !== showing) {
      image.srcset = '';
      image.src = variant.image;
    }
    showing = variant.id;

    if (open) {
      buttons.forEach((button) => (button.disabled = false));
      // The button names the action, not the step: Add to bag while a choice is still open as
      // well as after it is made. Pressing it refuses, marks the unpicked row and scrolls to it
      // (below), which is where "Choose a size" is actually said. There is no variant yet, so
      // the plain label stands in for the one that carries a price.
      labels(() => text.addPlain);
      say('');
    } else {
      buttons.forEach((button) => (button.disabled = !variant.available));
      labels(variant.available ? addLabel(variant) : () => text.soldOut);

      // The line names the size it is about: under a row of sizes, "Only 2 left" alone
      // leaves the shopper to work out which. A piece with nothing but a colour has none.
      const size = radios
        .filter((radio) => radio.checked && !('qvColour' in radio.dataset) && !radio.closest('[hidden]'))
        .map((radio) => radio.value)
        .join(' / ');

      if (!variant.available) {
        say(text.soldOut, 'out');
      } else if (variant.left !== null && variant.left > 0 && variant.left <= 3) {
        const line =
          variant.left === 1
            ? (size ? text.lastSize : text.last).replace('#', size)
            : (size ? text.lowSize : text.low).replace('@', variant.left).replace('#', size);
        say(line, 'low');
      } else {
        say('');
      }
    }

    announce(variant, open);
  };

  form.addEventListener('submit', (event) => {
    // Pressed before a size was picked: point at the sizes instead of adding. An
    // empty id must never post, with or without the shared script.
    if (!field.value) {
      event.preventDefault();
      const open = [...root.querySelectorAll('.qv__option')].filter((set) => !set.hidden);
      const unpicked = open.find((set) => !set.querySelector(':checked'));
      if (unpicked) {
        unpicked.classList.add('is-missing');
        // The radios are visually hidden, so focus alone would not bring the choices on screen.
        // The scroll goes to the LAST row and rests it at the foot of the view, so every choice
        // above it comes up with it: landing on the one that is missing would take the colours
        // off screen on the way to the sizes, and centring it scrolls further than either needs.
        // `scroll-margin-block-end` on the rows holds them clear of a bar pinned over the foot.
        open[open.length - 1].scrollIntoView({
          block: 'end',
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
        unpicked.querySelector('[data-qv-option]').focus({ preventScroll: true });
      }
      say(text.choose, 'choose');
      return;
    }

    // Without the shared script (assets/cart-add.js) the form posts as normal: Add to bag to the
    // cart, Buy now (a `return_to` submitter) on to checkout.
    if (!window.addToBag) return;
    event.preventDefault();
    if (event.submitter?.name === 'return_to') {
      window.buyNow(form, event.submitter, refuse);
      return;
    }
    // The sticky bar's button submits the same form from outside it.
    const button = buttons.includes(event.submitter) ? event.submitter : buttons[0];
    window.addToBag(form, button, button.querySelector('[data-qv-add-label]'), refuse);
  });

  // A product with nothing to choose is already correct as Liquid rendered it,
  // and asking the lookup to match an empty selection would find no variant --
  // except that Liquid always names the price, so a priceless panel drops it here.
  // A single-variant product page can keep its Add action disabled for an unpriced choice.
  if (!radios.length) {
    if (field.value && buttons[0] && !buttons[0].disabled) labels(addLabel(variants[0]));
    announce(variants[0], false);
    return;
  }

  guideButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const radio = radios.find(
        (candidate) =>
          candidate.value === button.dataset.sizeGuideSelect &&
          Number(candidate.dataset.qvOption) === Number(button.dataset.sizeGuideOption)
      );
      if (!radio || button.disabled) return;

      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      button.closest('[data-size-guide-dialog]')?.close();
      radio.focus({ preventScroll: true });
    });
  });

  radios.forEach((radio) => radio.addEventListener('change', sync));
  sync();
};

/*
 * Quick view.
 *
 * One dialog for the page, built the first time someone asks for it and reused
 * after that -- two grids on the homepage do not need two of them. The panel's
 * HTML comes from `sections/quick-view.liquid` through the Section Rendering
 * API. It is fetched when the pointer settles on a card, not when the pill is
 * pressed, so the panel opens already filled and there is no loading state.
 *
 * Choosing and adding is variantPicker above, which the product page shares: this
 * part only fetches the panel, opens it and closes it.
 */
(() => {
  const CACHE = new Map();
  let dialog = null;
  let opener = null;
  let latest = 0;

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'qv-dialog';
    dialog.setAttribute('aria-labelledby', 'qv-title');
    // critical.css already locks the page for an open [scroll-lock] dialog.
    dialog.setAttribute('scroll-lock', '');

    // A click that lands on the dialog element itself came from the backdrop;
    // anything inside the panel stops at its own box.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    // Covers the close button, Escape and the backdrop in one place.
    dialog.addEventListener('close', () => {
      dialog.replaceChildren();
      if (opener) opener.focus();
      opener = null;
    });

    dialog.addEventListener('variant:change', (event) => {
      const details = dialog.querySelector('[data-qv-details]');
      if (!details || !event.detail?.variant?.id) return;
      const url = new URL(details.href, location.origin);
      url.searchParams.set('variant', event.detail.variant.id);
      details.href = url;
    });

    document.body.append(dialog);
    return dialog;
  }

  function wire(root) {
    root.querySelector('[data-qv-close]').addEventListener('click', () => dialog.close());
    window.variantPicker(root);
  }

  /*
   * The panel is fetched while the pointer is still travelling, so pressing
   * the pill has nothing to wait for and there is no loading state to show.
   * The cache holds the request itself, not its answer, so a press landing
   * mid-flight joins the one already running instead of starting a second.
   */
  function prefetch(url) {
    if (CACHE.has(url)) return CACHE.get(url);

    const endpoint = new URL(url, location.origin);
    endpoint.searchParams.set('section_id', 'quick-view');

    const job = fetch(endpoint)
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(
        (markup) =>
          new DOMParser()
            .parseFromString(markup, 'text/html')
            .querySelector('#shopify-section-quick-view').innerHTML
      )
      .catch((error) => {
        // A failure must not be remembered, or one bad moment would poison
        // that card for the rest of the visit.
        CACHE.delete(url);
        throw error;
      });

    CACHE.set(url, job);
    return job;
  }

  async function open(pill) {
    const url = pill.dataset.quickView;
    const ticket = ++latest;

    let html;
    pill.setAttribute('aria-busy', 'true');
    try {
      html = await prefetch(url);
    } catch {
      // Never leave a press doing nothing. The product page is the thing
      // they were reaching for anyway.
      location.href = url;
      return;
    } finally {
      pill.removeAttribute('aria-busy');
    }

    // A second pill was pressed while this one was still on its way.
    if (ticket !== latest) return;

    const root = dialog || build();
    opener = pill;
    // The panel renders from the product's URL and cannot see the section it was opened
    // from, so that section says whether its prices are hidden (critical.css hides them).
    root.classList.toggle('qv-dialog--no-price', !!pill.closest('[data-show-prices="false"]'));
    root.innerHTML = html;
    wire(root);
    root.showModal();
    root.querySelector('[data-qv-close]').focus();
  }

  document.addEventListener('click', (event) => {
    const pill = event.target.closest('[data-quick-view]');
    if (!pill) return;
    // The hero and story openers are links to the product page, for pages that
    // render no grid and so load none of this. Here the panel replaces that
    // navigation, unless the shopper asked for a new tab.
    if (pill.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey)) return;
    event.preventDefault();
    open(pill);
  });

  // A phone has no hover to fetch on, so the press itself starts the fetch and
  // the click that follows finds the panel already on its way.
  document.addEventListener('pointerdown', (event) => {
    const pill = event.target.closest('[data-quick-view]');
    if (pill) prefetch(pill.dataset.quickView).catch(() => {});
  });

  // Settling on a card is the intent signal: the pill has not even faded in
  // yet. The short dwell keeps a pointer sweeping across the grid from
  // fetching every product it passes over.
  if (matchMedia('(hover: hover)').matches) {
    let dwell;

    document.addEventListener('pointerover', (event) => {
      const card = event.target.closest('.product-card');
      const pill = card && card.querySelector('[data-quick-view]');
      clearTimeout(dwell);
      if (pill) dwell = setTimeout(() => prefetch(pill.dataset.quickView).catch(() => {}), 80);
    });
  }

  // Tab reaches the pill without any pointer ever touching the card.
  document.addEventListener('focusin', (event) => {
    const pill = event.target.closest('[data-quick-view]');
    if (pill) prefetch(pill.dataset.quickView).catch(() => {});
  });
})();
